import { Component, Input, ViewChild, ElementRef, AfterViewInit, EventEmitter, Output } from '@angular/core';

import { isNumber, isNil } from 'lodash';

import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';


/**
 * ### Accessibility Usage
 * + ***ariaDescribedBy*** - use this if the number input has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***ariaLabelledBy*** - use this to associate an external label with the number input. Use id of the label as this value
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***initialFocus*** - set to true to make this input have focus when it is created
 */
@Component({
    selector: 'agpr-number-input',
    templateUrl: './number-input.component.html',
    styleUrls: ['./number-input.component.scss'],
    providers: [getProvider(NumberInputComponent)],
    standalone: false
})
export class NumberInputComponent extends ControlValueAccessorModel<number> implements AfterViewInit {
    @Input() public id: string;
    @Input() public isEditing: boolean = true;
    @Input() public label: string;
    @Input() public placeholder: string;
    @Input() public ariaLabelledBy: string;
    @Input() public tooltip: string;
    @Input() public required: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public icon: string;
    @Input() public unit: string;
    @Input() public initialFocus: boolean = false;
    @Input() public bottomMargin: boolean = true;
    @Input() public min: number;
    @Input() public max: number;
    @Input() public step: number = 1;
    // If the value is set, when an invalid value is input, it would default to this value
    @Input() public defaultValue: number;
    @Input() public integerOnly: boolean = false;
    @Input() public allowedDecimalUnits: number = 0;
    // when we want to value to be undefined instead of setting it to min, set forceMinValue to false
    @Input() public forceMinValue: boolean = true;
    @Input() public labelIcon: string;
    @Input() public ariaDescribedBy: string;
    @Input() public enableClamp: boolean = true;
    @Input() public showArrows: boolean = true;
    @Input() public useRemUnit: boolean = false;

    unitOffset: number = 0;
    remOffset: number = 0;
    containerElement: HTMLDivElement;

    // `change`, `keyup` and `keydown` are handled automatically by angular + browser
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() changes: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('container') container: ElementRef;
    @ViewChild('input') input: ElementRef;


    ngAfterViewInit(): void {
        if (this.initialFocus) {
            this.giveFocus();
        }
        this.containerElement = this.container.nativeElement;
        requestAnimationFrame(() => this.calculateUnit(this.value));
    }

    giveFocus() {
        if (this.input) {
            requestAnimationFrame(() => this.input.nativeElement.focus());
        }
    }

    /**
     * onKeydown event
     */
    onKeydown(event: KeyboardEvent): boolean {
        // block 'e' and 'E' on keydown
        if (event.code === 'KeyE') {
            return false;
        }

        // if the input only accepts integer, block '.' on keydown
        if (event.key === '.' && this.integerOnly) {
            return false;
        }

        this.keydown.emit(event);
    }

    onFocus(event: FocusEvent) {
        this.focus.emit(event);
    }

    computeChanges(useInputValue: boolean = false) {
        let value = useInputValue ? this.input.nativeElement.value : this.value;
        if (this.allowedDecimalUnits > 0) {
            const valueString = value + '';
            if (valueString.includes('.') && (this.countDecimals(Number(value))) > this.allowedDecimalUnits) {
                value = Number(value).toFixed(this.allowedDecimalUnits);
                this.value = Number(value);
                this.input.nativeElement.value = this.value;
            }
        }
        this.calculateUnit(value);
        this.changes.emit(this.value);
        this.onModelChange(this.value);
    }

    countDecimals(value: number): number {
        if (Math.floor(value) === value) {
            return 0;
        }
        return value.toString().split('.')?.[1]?.length || 0;
    }

    upArrowClick(): void {
        this.input.nativeElement.focus();

        const aboveMin = isNil(this.min) || this.value >= this.min;
        const belowMax = isNil(this.max) || this.value < this.max;

        if (aboveMin && belowMax) {
            this.value += this.step;

            const decimals = this.allowedDecimalUnits > 0 ? this.allowedDecimalUnits : 2;
            const factor = 10 ** decimals;

            this.value = Math.round(this.value * factor) / factor;
        }

        if (isNil(this.value) || !Number.isFinite(this.value)) {
            this.value = this.min;
        }

        this.computeChanges();
    }

    downArrowClick(): void {
        this.input.nativeElement.focus();

        const aboveMin = isNil(this.min) || this.value > this.min;
        const belowMax = isNil(this.max) || this.value <= this.max;

        if (aboveMin && belowMax) {
            this.value -= this.step;
            const decimals = this.allowedDecimalUnits > 0 ? this.allowedDecimalUnits : 2;
            const factor = 10 ** decimals;
            this.value = Math.round(this.value * factor) / factor;
        }

        if (isNil(this.value) || !Number.isFinite(this.value)) {
            this.value = this.min;
        }

        this.computeChanges();
    }

    // Nudges the value back to one that is correct depending on the restrictions of the input
    private clampValue(): void {
        if (this.integerOnly && !!this.value && !Number.isInteger(this.value)) {
            this.value = Math.round(this.value);
            this.computeChanges();
        }

        // when min is set, if value is null or undefined, set it to min
        // when min is set, if value is a solid number, but less than min, set it to min
        if (this.min !== undefined && this.min !== null) {
            if ((this.forceMinValue) && (this.value === null || this.value === undefined || this.value < this.min)) {
                if (this.defaultValue !== null && this.defaultValue !== undefined ) {
                    this.value = this.defaultValue;
                } else {
                    this.value = this.min;
                }
                this.computeChanges();
            } else if (this.value !== null && this.value !== undefined && this.value < this.min) {
                this.value = this.min;
                this.computeChanges();
            }
        }

        if (this.max !== undefined && this.max !== null && !!this.value && this.value > this.max) {
            this.value = this.max;
            this.computeChanges();
        }
    }

    calculateUnit(value?: number | string) {
        if (!this.unit || !this.input || (!value && value !== 0)) {
            return;
        }

        const valueString = value + '';
        const length = valueString.length;
        this.unitOffset = length * 8;

        if (length <= 3) {
            this.remOffset = 24;
        } else if (length === 4) {
            this.remOffset = 28 ;
        } else {
            this.remOffset = 32;
        }

        // Decimals are about half width
        if (valueString.includes('.')) {
            this.unitOffset -= 4;
        }
    }

    /**
     * Prevents the UI from moving the focus to one of the arrow buttons
    */
    preventArrowFocus(event: Event): void {
        event.preventDefault();
    }

    onBlur(event: FocusEvent): void {
        if (this.enableClamp) {
            this.clampValue();
        }
        this.blur.emit(event);
    }

    writeValue(value: number) {
        this.calculateUnit(value);
        this.value = value;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }
}
